// ABOUTME: Dialog resolving a row another user saved while this user had an editor open on it:
// ABOUTME: compares the incoming values with the stored ones and lets the user keep or accept.

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@ds/coolshop';
import { BaseTable, type SearchNode } from '@ds/data-table';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ColumnDef } from '@tanstack/react-table';
import { MouseEvent, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getEditColumnId, type RemoteConflict, toNodeContent } from './hooks/useRemoteRowUpdates';
import {
    findContentType,
    getContentTypeLabel,
    getGroupLabel,
    getValueLabel,
    RemoteUpdatesSchema,
} from './remoteUpdatesLabels';

const COLUMNS = [
    {
        id: 'contentTypeGroup',
        messageId: 'data-table.remote-conflict.column.content-type-group',
        defaultMessage: 'Content Type Group',
    },
    {
        id: 'contentType',
        messageId: 'data-table.remote-conflict.column.content-type',
        defaultMessage: 'Content Type',
    },
    {
        id: 'incomingValue',
        messageId: 'data-table.remote-conflict.column.incoming-value',
        defaultMessage: 'Incoming Value',
    },
    {
        id: 'currentValue',
        messageId: 'data-table.remote-conflict.column.current-value',
        defaultMessage: 'Current Value',
    },
    {
        id: 'conflict',
        messageId: 'data-table.remote-conflict.column.conflict',
        defaultMessage: 'Conflict',
    },
] as const;

type ConflictRow = {
    id: string;
    contentTypeGroup: string;
    contentType: string;
    incomingValue: string;
    currentValue: string;
    conflict: boolean;
};

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '');

// The inline editor with the pending edit commits on blur, so the dialog must never take the focus
// from it: opening it would save the draft over the incoming change before the user chose. The DS
// Dialog forwards these to MUI at runtime without declaring them, hence the cast.
const KEEP_EDITOR_FOCUS = {
    disableAutoFocus: true,
    disableEnforceFocus: true,
    disableRestoreFocus: true,
} as Record<string, boolean>;

// Same reason for the buttons: a mousedown would move the focus, and the blur would commit the
// draft before "Accept incoming" replaces it.
const preventFocusSteal = (event: MouseEvent) => event.preventDefault();

type RemoteConflictDialogProps = {
    conflict?: RemoteConflict;
    schema?: RemoteUpdatesSchema;
    defaultLanguage: string;
    getNode: (nodeId: string) => SearchNode | undefined;
    onKeepMine: (nodeId: string) => void;
    onAccept: (nodeId: string) => void;
};

export function RemoteConflictDialog({
    conflict,
    schema,
    defaultLanguage,
    getNode,
    onKeepMine,
    onAccept,
}: RemoteConflictDialogProps) {
    const intl = useIntl();
    const { searchDataPagination } = useContext(SettingsContext);

    const columns = useMemo(() => {
        const yes = intl.formatMessage({ id: 'yes', defaultMessage: 'Yes' });
        const no = intl.formatMessage({ id: 'no', defaultMessage: 'No' });
        const none = intl.formatMessage({ id: 'none', defaultMessage: 'None' });

        return COLUMNS.map(({ id, messageId, defaultMessage }) => ({
            id,
            header: intl.formatMessage({ id: messageId, defaultMessage }),
            cell: ({ row }) => {
                if (id === 'conflict') return row.original.conflict ? yes : no;
                return row.original[id] || none;
            },
        })) satisfies ColumnDef<ConflictRow>[];
    }, [intl]);

    // The current value is the one the grid stores, not the draft of the open editor.
    const rows = useMemo<ConflictRow[]>(() => {
        if (!conflict) return [];
        const node = getNode(conflict.nodeId);

        return conflict.edits.flatMap(edit => {
            const ct = findContentType(schema, edit.contentTypeGroup, edit.contentType);
            const incoming = toNodeContent(edit);
            const stored = node?.contents?.find(
                c =>
                    c.contentTypeGroup === edit.contentTypeGroup &&
                    c.contentType === edit.contentType &&
                    (c.contentTypeGroupIndex ?? 0) === (edit.contentTypeGroupIndex ?? 0),
            );
            const languages = ct?.multiLanguage
                ? incoming.value.map(v => v.languageIsoCode)
                : [defaultLanguage];

            const toLabel = (values: unknown[] | undefined, language: string) => {
                const label = (values ?? [])
                    .map(value => getValueLabel(ct, value, language))
                    .join(', ');
                return edit.type === 'html' ? stripHtml(label) : label;
            };

            return languages
                .map(language => ({
                    id: `${getEditColumnId(edit)}-${edit.contentTypeGroupIndex}-${language}`,
                    contentTypeGroup: getGroupLabel(schema, edit.contentTypeGroup, defaultLanguage),
                    contentType: getContentTypeLabel(
                        schema,
                        edit.contentTypeGroup,
                        edit.contentType,
                        defaultLanguage,
                    ),
                    incomingValue: toLabel(
                        incoming.value.find(v => v.languageIsoCode === language)?.value,
                        language,
                    ),
                    currentValue: toLabel(
                        stored?.value?.find(v => v.languageIsoCode === language)?.value,
                        language,
                    ),
                    conflict: conflict.openColumns.includes(getEditColumnId(edit)),
                }))
                .filter(row => row.incomingValue !== row.currentValue);
        });
    }, [conflict, schema, defaultLanguage, getNode]);

    return (
        <Dialog
            open={!!conflict}
            onClose={() => conflict && onKeepMine(conflict.nodeId)}
            maxWidth="md"
            fullWidth
            {...KEEP_EDITOR_FOCUS}
        >
            <DialogTitle>
                {intl.formatMessage({
                    id: 'incoming-edits',
                    defaultMessage: 'Someone modified this node',
                })}
                {conflict ? ` — ${conflict.code}` : ''}
            </DialogTitle>
            <DialogContent>
                <BaseTable
                    id="remote-conflict"
                    data={rows}
                    columns={columns}
                    count={rows.length}
                    handleStateChange={() => {}}
                    selectedRows={[]}
                    initialFilters={[]}
                    disabledFilters={[]}
                    hasNextPage={false}
                    settings={{ pagination: searchDataPagination || undefined }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    onMouseDown={preventFocusSteal}
                    onClick={() => conflict && onKeepMine(conflict.nodeId)}
                >
                    {intl.formatMessage({
                        id: 'data-table.remote-conflict.keep-mine',
                        defaultMessage: 'Keep mine',
                    })}
                </Button>
                <Button
                    variant="contained"
                    onMouseDown={preventFocusSteal}
                    onClick={() => conflict && onAccept(conflict.nodeId)}
                >
                    {intl.formatMessage({
                        id: 'data-table.remote-conflict.accept-incoming',
                        defaultMessage: 'Accept incoming',
                    })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
